import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, ChevronDown, Target } from "lucide-react";
import { API } from "@/auth";
import useSeo from "@/seo";

function displayDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return iso || "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

const OVERVIEW_ROWS = [
  ["Game Type", "Traditional archery based number game"],
  ["Popular Region", "Shillong, Meghalaya"],
  ["Result Method", "Based on arrow count"],
  ["Daily Results", "Released according to the official schedule"],
  ["Number Range", "Two-digit winning number"],
  ["Interest Level", "Very high among regular followers"],
  ["Result Tracking", "Online and offline"],
  ["Analysis", "Weekly and monthly reviews"],
];

const COMPARE_ROWS = [
  ["Based on Archery", "Yes", "No"],
  ["Live Event", "Yes", "Usually No"],
  ["Cultural Heritage", "Strong", "Limited"],
  ["Daily Results", "Yes", "Depends on the game"],
  ["Community Discussions", "Very Active", "Varies"],
];

const ANALYSIS_STEPS = [
  "First, find the previous results of the morning teer",
  "Note the repeated counter numbers of the morning teer",
  "Find the common numbers of the morning teer",
  "Match the morning teer dream numbers",
  "Match any missing numbers with the previous results",
  "Follow the weekly trend",
];

const FAQ = [
  ["What is the result of Morning Teer?",
    "The result of Morning Teer is that all the archers calculate the time between the arrows and release the teer, and it hits two numbers. Two rounds are played consecutively; the two numbers that hit are considered the result of Morning Teer."],
  ["What is Morning Sunday Teer?",
    "Morning Sunday Teer refers to a traditional archery game played on Sunday morning. Its result is revealed by the archers shooting two arrows to determine the winning numbers."],
  ["How is Shillong Morning Teer different from other games?",
    "In other games where a computer or software base is used, results are generated digitally. In Shillong Morning Teer real archers shoot arrows and the winning numbers are determined by their skill."],
  ["Can the record of previous Morning Teer results affect future results?",
    "No, previous results cannot decide future ones. Many followers still study earlier records to observe trends and understand how numbers behave over time."],
  ["Why do people follow the Morning Shillong Teer results every day?",
    "People enjoy the traditional archery background, the daily analysis of numbers, and the strong social bond surrounding the game."],
  ["Is Morning Teer popular only in Meghalaya?",
    "Morning Teer originated in Shillong, Meghalaya, but many people from different regions follow the latest results and historical updates online."],
  ["What is the best way to check the official Morning Teer results?",
    "The most reliable place to check is our website — updates are published as soon as the archery round is over."],
  ["Why do players keep records of previous Morning Teer results?",
    "Because many people believe number patterns may repeat. They compare daily, weekly and monthly results and enjoy tracking the trend."],
];

export default function InfoPage() {
  const [result, setResult] = useState(null);
  const nav = useNavigate();

  useSeo({
    title: "Morning Sunday Teer Result — Complete Guide & FAQs | Morning Sunday Teer",
    description: "Complete guide to Shillong Morning Teer — how it works, result times, dream numbers, weekly analysis, comparisons and FAQs.",
    path: "/info",
  });

  useEffect(() => {
    document.title = "Morning Sunday Teer Result | Morning Teer & Shillong Morning Teer";
    axios.get(`${API}/results/latest`).then((r) => setResult(r.data)).catch(() => {});
  }, []);

  const todayDate = result?.date ? displayDate(result.date) : new Intl.DateTimeFormat("en-GB").format(new Date());

  return (
    <div className="info-shell" data-testid="info-page">
      {/* Compact top bar */}
      <header className="info-topbar">
        <button className="info-back" data-testid="info-back-button" onClick={() => nav("/")}>
          <ArrowLeft size={16} /> Home
        </button>
        <div className="info-brand">
          <span className="info-brand-mark"><Target size={16} /></span>
          <strong>Morning Sunday Teer</strong>
        </div>
        <span className="info-topbar-spacer" />
      </header>

      <main className="info-container">
        <h1 className="info-hero-title" data-testid="info-hero-title">
          Morning Sunday Teer Result {todayDate} | Morning Teer Result & Shillong Morning Teer
        </h1>
        <p className="info-hero-sub">
          Welcome to Morning Sunday Teer. Get the fastest daily updates for Morning Teer and Shillong Morning Teer results, common numbers, dream numbers, previous records and useful information — all in one clean place.
        </p>

        {/* Today's Result mini-card */}
        <section className="info-result-card" data-testid="info-result-card" aria-label="Latest result">
          <div className="info-result-head">
            <strong>Morning Sunday Teer Result</strong>
            <span><CalendarDays size={14} /> {todayDate}</span>
          </div>
          <div className="info-result-row">
            <div>
              <small>F/R (10:30 AM)</small>
              <span className="info-result-num" data-testid="info-fr">{result?.first_round || "--"}</span>
            </div>
            <div>
              <small>S/R (11:30 AM)</small>
              <span className="info-result-num" data-testid="info-sr">{result?.second_round || "--"}</span>
            </div>
          </div>
          <Link to="/previous-results" className="info-result-link" data-testid="info-check-previous">
            Check Previous Results <ArrowRight size={15} />
          </Link>
        </section>

        <p>
          If you follow Shillong's teer game, you probably also follow the <b>Shillong Morning Teer Result</b> and want to check the numbers at least once a day. For many, visiting the site each morning has become part of the local routine and culture.
        </p>
        <p>
          The popularity of <b>Morning Teer</b>, <b>Shillong Morning Teer</b> and <b>Morning Sunday Teer</b> is steadily rising in Shillong because the game is not computer-based or a mobile casino — it is played by real archers, and that traditional method has grown into a widely followed number game.
        </p>

        <h2 className="info-h2">Overview of Shillong Morning Teer</h2>
        <div className="info-table-wrap">
          <table className="info-table">
            <thead><tr><th>Topic</th><th>Details</th></tr></thead>
            <tbody>{OVERVIEW_ROWS.map(([k, v]) => (
              <tr key={k}><td>{k}</td><td>{v}</td></tr>
            ))}</tbody>
          </table>
        </div>
        <p className="info-caption">
          This brief overview shows why <b>Shillong Morning Teer</b> is one of the most popular archery-based number games in Shillong, Meghalaya.
        </p>

        <h2 className="info-h2">What is Morning Teer?</h2>
        <p>
          Morning Teer is a numbers game based on archery, played mainly in Shillong, Meghalaya. It is not like a simple lottery — the winning number comes from a real archery competition in which trained archers shoot hundreds of arrows at a target. Officials carefully count each arrow, and the final total becomes the official Morning Teer result.
        </p>
        <p>
          This old method gives the game a special character. It is a blend of tradition, skill and social participation rather than a machine-driven activity.
        </p>

        <h2 className="info-h2">Why do people search for Morning Sunday Teer?</h2>
        <p>
          Many people search for <b>Morning Sunday Teer</b> especially on Sundays, when free time is plentiful and the day's numbers attract extra attention. They also love comparing past results with today's numbers and looking up common or dream numbers before the round.
        </p>
        <p>
          For some it is a long-standing hobby; for others it has become a small daily habit — a cup of tea and a quick check of the morning numbers.
        </p>

        <h2 className="info-h2">How does Morning Teer work?</h2>
        <p>
          Morning Teer is popular because the process is simple. Experienced archers gather in a shooting round and take aim at their targets. When the round ends, officials count every arrow and tally the hits. The final two-digit number becomes the official Morning Teer result.
        </p>
        <p>
          The archers shoot in two sessions — one around <b>10:30 AM</b> and the other around <b>11:30 AM</b> — so two numbers are announced each day.
        </p>

        <h2 className="info-h2">Why is Shillong Morning Teer different from others?</h2>
        <p>
          Many number games rely on computers or software to pick winning numbers. Shillong Morning Teer is different — real archers determine the outcome, keeping visitors connected with a living cultural tradition.
        </p>

        <h2 className="info-h2">Why do the results of Morning Teer differ every day?</h2>
        <p>
          No archer can shoot the same arrow twice. Weather, the archer's focus and small differences in shooting time all influence where each arrow lands, so the final number naturally changes from day to day. That unpredictability is a big part of the game's charm.
        </p>

        <h2 className="info-h2">When are Morning Teer results published?</h2>
        <p>
          Morning Teer publishes the results at two times — <b>10:30 AM</b> and <b>11:30 AM</b>. Announcements are made after the archers finish shooting and officials count every arrow. Many visitors set a daily routine around this window so they never miss the update.
        </p>

        <h2 className="info-h2">Cultural significance of Shillong Morning Teer</h2>
        <p>
          Shillong Morning Teer is more than numbers — it is deeply linked to the traditional archery of Meghalaya. Archery is respected as both a cultural activity and a sport, and Teer keeps the tradition alive while remaining approachable to modern audiences. That balance between heritage and daily interest is why the game remains so popular today.
        </p>

        <h2 className="info-h2">How to read the Morning Teer numbers</h2>
        <p>
          Understanding the numbers takes a little study. Many regular followers of the Morning Teer results feel that reviewing past outcomes helps them recognise how numbers behave over time.
        </p>
        <p>
          No number game can guarantee future outcomes, but analysing history has become a tradition in itself — which is why so many visitors look for <b>Shillong Morning Teer Previous Results</b>. Sometimes today's numbers echo yesterday's, but most days are refreshingly unique.
        </p>

        <h2 className="info-h2">Popular ways to analyse Morning Teer results</h2>
        <p>
          When reviewing history, followers usually try a mix of these methods:
        </p>
        <ul className="info-list">
          {ANALYSIS_STEPS.map((s) => <li key={s}>{s}</li>)}
        </ul>
        <p>
          Combining these approaches makes the Shillong Morning Teer game even more interesting to follow.
        </p>

        <h2 className="info-h2">Morning Sunday Teer weekly analysis</h2>
        <p>
          Weekly analysis lets followers watch short-term shifts in the number pattern instead of focusing on a single day. Players note recurring numbers and interesting positions, then compare them across the next seven days. Sometimes a specific number shows up more than once in a week; other weeks look completely different.
        </p>
        <p>
          These observations become talking points inside the community. Weekly trends can't predict tomorrow, but checking them has become part of many players' routine.
        </p>

        <h2 className="info-h2">Comparison with other number games</h2>
        <div className="info-table-wrap">
          <table className="info-table">
            <thead><tr><th>Feature</th><th>Shillong Morning Teer</th><th>Typical Number Games</th></tr></thead>
            <tbody>{COMPARE_ROWS.map(([f, a, b]) => (
              <tr key={f}><td>{f}</td><td>{a}</td><td>{b}</td></tr>
            ))}</tbody>
          </table>
        </div>

        <h2 className="info-h2">Tips for Morning Sunday Teer players</h2>
        <p>If you are new to Morning Sunday Teer, a few small habits will make the game more enjoyable:</p>
        <ul className="info-list">
          <li>Learn how the results are calculated from the archery rounds</li>
          <li>Follow a trusted results platform — our site is updated as soon as counts finish</li>
          <li>Track previous results together with common numbers and dream numbers</li>
          <li>Stay curious — the experience grows the more you observe</li>
        </ul>

        <h2 className="info-h2">Why is Shillong Morning Teer so popular?</h2>
        <p>
          Interest in Shillong Morning Teer now reaches far beyond Meghalaya. People from many regions look up the results every day, and the internet has made the numbers easy to check anywhere. On the other side, the traditional archers keep the game charming and authentic. That mix of history and modern access is what carries the popularity across generations.
        </p>

        <h2 className="info-h2">FAQs about Morning Sunday Teer</h2>
        <div className="info-faq" data-testid="info-faq">
          {FAQ.map(([q, a], i) => (
            <details key={q} className="info-faq-item" data-testid={`info-faq-${i + 1}`}>
              <summary>
                <span>{i + 1}. {q}</span>
                <ChevronDown className="info-faq-caret" size={18} />
              </summary>
              <p>{a}</p>
            </details>
          ))}
        </div>

        <h2 className="info-h2">Final thoughts</h2>
        <p>
          Morning Teer results are more than a daily number announcement — they carry a slice of culture. Archers decide the outcome through their own skill, giving the game a physical, human quality that other number games simply do not have.
        </p>
        <p>
          The steady popularity of Shillong Morning Teer shows how much people value the unique mix of culture, history and daily excitement. If you are new to this world, understanding how the system works will make your visits more meaningful — follow trusted result sources, review the history, and enjoy the tradition.
        </p>

        <div className="info-bottom-nav">
          <Link to="/previous-results" className="info-pill" data-testid="info-nav-previous">Previous Results</Link>
          <Link to="/common-numbers" className="info-pill" data-testid="info-nav-common">Common Number</Link>
          <Link to="/dream-numbers" className="info-pill" data-testid="info-nav-dream">Dream Numbers</Link>
          <Link to="/" className="info-pill info-pill-dark" data-testid="info-nav-home">Back to Home</Link>
        </div>
      </main>

      <footer className="info-footer">
        <span>© 2026 Morning Sunday Teer. All Rights Reserved.</span>
      </footer>
    </div>
  );
}
